/**
 * useFilterOptions — opções de filtro do BANCO INTEIRO.
 *
 * GET /api/nichos-virais/filter-options → { languages[], categories[],
 * monetized_count, faceless_count, total }. Cada idioma/categoria vem com
 * { value, count } (já com filtros invisíveis aplicados no backend).
 *
 * Resolve o bug "filtros só mostram o que carregou": antes as opções eram
 * derivadas dos ~30-90 canais em memória; agora vêm do banco inteiro
 * (25 idiomas, 49 categorias).
 *
 * Cache em memória de módulo (compartilhado entre instâncias). TTL 5min.
 */
import { useState, useEffect } from 'react';
import axios from 'axios';

const API = (import.meta.env.VITE_BACKEND_URL || '') + '/api';

let _cachedOptions = null;
let _cachedAt = 0;
const TTL_MS = 5 * 60 * 1000;

// `enabled=false` segura o fetch (ex.: usuário sem acesso ao Niche Finder —
// o endpoint é premium e responderia 403; sem o gate, todo free gerava um
// request inútil + erro no console ao abrir o modo teaser).
export function useFilterOptions({ enabled = true } = {}) {
  const [options, setOptions] = useState(_cachedOptions);
  const [loading, setLoading] = useState(!_cachedOptions);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return undefined;
    }
    const fresh = _cachedOptions && (Date.now() - _cachedAt) < TTL_MS;
    if (fresh) {
      setOptions(_cachedOptions);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(
          `${API}/nichos-virais/filter-options`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        _cachedOptions = data;
        _cachedAt = Date.now();
        setOptions(data);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(err);
        // eslint-disable-next-line no-console
        console.error('useFilterOptions error:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [enabled]);

  return { options, loading, error };
}
